/**
 * Accessible Navigation Component - Clean Implementation
 * Mouse: Click to toggle, no hover
 * Keyboard: Full tab/arrow navigation with proper exit behavior
 */
export class AccessibleNavigation {
    constructor() {
        this.mobileBtn = document.querySelector('.mobile-menu-btn');
        this.mainNav = document.querySelector('.main-nav');
        this.dropdownToggles = document.querySelectorAll('.nav-link-drop');
        
        this.init();
    }

    init() {
        this.setupMobileMenu();
        this.setupDropdowns();
        this.setupClickOutside();
        this.setupResizeHandler();
    }

    setupMobileMenu() {
        if (!this.mobileBtn) return;
        
        this.mobileBtn.addEventListener('click', () => {
            const isExpanded = this.mobileBtn.getAttribute('aria-expanded') === 'true';
            
            this.mobileBtn.setAttribute('aria-expanded', !isExpanded);
            this.mainNav.classList.toggle('show');
            
            this.mobileBtn.textContent = !isExpanded ? 'Chiudi' : 'Menu';
            this.mobileBtn.setAttribute('aria-label', 
                !isExpanded ? 'Chiudi menu di navigazione' : 'Apri menu di navigazione'
            );
        });
    }

    setupDropdowns() {
        this.dropdownToggles.forEach(toggle => {
            const navItem = toggle.closest('.nav-item');
            const dropdown = navItem?.querySelector('.nav-submenu');
            const dropdownLinks = dropdown?.querySelectorAll('.nav-sublink');
            
            if (!navItem || !dropdown || !dropdownLinks) return;

            // MOUSE: Click to toggle, no hover
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                
                if (isOpen) {
                    this.closeDropdown(toggle, dropdown);
                } else {
                    this.openDropdown(toggle, dropdown);
                }
            });

            // KEYBOARD: Toggle button navigation
            toggle.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                    
                    if (isOpen) {
                        this.closeDropdown(toggle, dropdown);
                    } else {
                        this.openDropdown(toggle, dropdown);
                        // Focus first submenu item when opened with keyboard
                        setTimeout(() => {
                            dropdownLinks[0].focus();
                        }, 50);
                    }
                } else if (e.key === 'Escape') {
                    this.closeDropdown(toggle, dropdown);
                }
            });

            // KEYBOARD: Submenu navigation
            dropdownLinks.forEach((link, index) => {
                link.addEventListener('keydown', (e) => {
                    const isFirstItem = index === 0;
                    const isLastItem = index === dropdownLinks.length - 1;
                    
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        
                        if (isLastItem) {
                            // Exit submenu and go to next main nav item
                            this.closeDropdown(toggle, dropdown);
                            this.focusNextMainNavItem(toggle);
                        } else {
                            // Move to next submenu item
                            dropdownLinks[index + 1].focus();
                        }
                        
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        
                        if (isFirstItem) {
                            // Go back to toggle button
                            toggle.focus();
                        } else {
                            // Move to previous submenu item
                            dropdownLinks[index - 1].focus();
                        }
                        
                    } else if (e.key === 'Tab' && !e.shiftKey && isLastItem) {
                        // Tab out from last item: close dropdown and continue naturally
                        this.closeDropdown(toggle, dropdown);
                        
                    } else if (e.key === 'Tab' && e.shiftKey && isFirstItem) {
                        // Shift+Tab from first item: close dropdown and go back to toggle
                        this.closeDropdown(toggle, dropdown);
                        
                    } else if (e.key === 'Escape') {
                        // Always available: close and return to toggle
                        this.closeDropdown(toggle, dropdown);
                        toggle.focus();
                    }
                });
            });
        });
    }

    setupClickOutside() {
        // Handle both touch and mouse events properly
        let touchStarted = false;
        let touchMoved = false;
        let touchStartY = 0;
        
        // Track touch start to differentiate from mouse events
        document.addEventListener('touchstart', (e) => {
            touchStarted = true;
            touchMoved = false;
            touchStartY = e.touches[0].clientY;
        }, { passive: true });
        
        // Track if user is scrolling vs tapping
        document.addEventListener('touchmove', (e) => {
            if (touchStarted) {
                const touchCurrentY = e.touches[0].clientY;
                const scrollDistance = Math.abs(touchCurrentY - touchStartY);
                // Consider it a scroll if moved more than 10px
                if (scrollDistance > 10) {
                    touchMoved = true;
                }
            }
        }, { passive: true });
        
        // Reset touch flag on mouse events
        document.addEventListener('mousedown', () => {
            touchStarted = false;
            touchMoved = false;
        });
        
        // Unified handler for click/touch events
        const handleOutsideInteraction = (e) => {
            // Close all dropdowns when clicking outside nav items
            if (!e.target.closest('.nav-item') && !e.target.closest('#theme-toggle')) {
                this.closeAllDropdowns();
            }
            
            // Close mobile menu when clicking outside header, but NOT when interacting with theme toggle
            // Also prevent closure during scroll gestures
            if (!e.target.closest('.site-header') && !e.target.closest('#theme-toggle') && this.mobileBtn && !touchMoved) {
                this.closeMobileMenu();
            }
        };
        
        // Use click for mouse, touchend for touch
        document.addEventListener('click', (e) => {
            if (!touchStarted) {
                handleOutsideInteraction(e);
            }
        });
        
        document.addEventListener('touchend', (e) => {
            if (touchStarted) {
                // Small delay to prevent conflicts with other touch interactions
                setTimeout(() => {
                    // Only handle as interaction if it wasn't a scroll gesture
                    if (!touchMoved) {
                        handleOutsideInteraction(e);
                    }
                    touchStarted = false;
                    touchMoved = false;
                }, 50);
            }
        }, { passive: true });
    }

    openDropdown(toggle, dropdown) {
        if (!toggle || !dropdown) return;
        
        // Close other dropdowns first
        this.closeAllDropdowns();
        
        toggle.setAttribute('aria-expanded', 'true');
        dropdown.classList.add('show');
    }

    closeDropdown(toggle, dropdown) {
        if (!toggle || !dropdown) return;
        
        toggle.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('show');
    }

    closeAllDropdowns() {
        this.dropdownToggles.forEach(toggle => {
            const navItem = toggle.closest('.nav-item');
            const dropdown = navItem?.querySelector('.nav-submenu');
            if (dropdown) {
                this.closeDropdown(toggle, dropdown);
            }
        });
    }

    setupResizeHandler() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Close mobile menu on resize to prevent layout issues
                if (this.mainNav.classList.contains('show')) {
                    this.closeMobileMenu();
                }
                this.closeAllDropdowns();
            }, 150);
        });
    }

    closeMobileMenu() {
        if (!this.mobileBtn || !this.mainNav) return;
        
        this.mobileBtn.setAttribute('aria-expanded', 'false');
        this.mainNav.classList.remove('show');
        this.mobileBtn.textContent = 'Menu';
        this.mobileBtn.setAttribute('aria-label', 'Apri menu di navigazione');
    }

    focusNextMainNavItem(currentToggle) {
        // Find all main navigation focusable elements
        const mainNavItems = Array.from(this.mainNav.querySelectorAll('a, button')).filter(el => {
            // Only main level items, not submenu items
            return !el.closest('.nav-submenu');
        });
        
        const currentIndex = mainNavItems.indexOf(currentToggle);
        const nextIndex = currentIndex + 1;
        
        if (nextIndex < mainNavItems.length) {
            // Focus next main nav item
            mainNavItems[nextIndex].focus();
        } else {
            // If it's the last item, let focus naturally continue outside nav
            // This could be the theme toggle or next section
            const themeToggle = document.getElementById('theme-toggle');
            if (themeToggle) {
                themeToggle.focus();
            }
        }
    }
}